import { useCallback, useRef } from 'react';
import { Task } from '../../types';
import { formatDate, parseDate, diffDays, todayStr } from '../../utils/dateUtils';
import { calculateInitialGanttScroll } from '../../components/gantt/ganttScrollUtils';

interface UseGanttScrollAndZoomParams {
  visibleTasks: Task[];
  visibleTasksRef: React.MutableRefObject<Task[]>;
  gridStart: Date;
  dateToIndexMap: Record<string, number>;
  dateToIndexMapRef: React.MutableRefObject<Record<string, number>>;
  dayWidth: number;
  dayWidthRef: React.MutableRefObject<number>;
  setDayWidth: React.Dispatch<React.SetStateAction<number>>;
  isFitMode: boolean;
  setIsFitMode: React.Dispatch<React.SetStateAction<boolean>>;
  selectedTaskId: number | string | null;
  setSelectedTaskId: React.Dispatch<React.SetStateAction<number | string | null>>;
  scrollContainerRef: React.RefObject<HTMLDivElement | null>;
  sidebarListRef: React.MutableRefObject<{ element?: HTMLDivElement | null; [key: string]: unknown } | null>;
  ganttListRef: React.MutableRefObject<{ element?: HTMLDivElement | null; [key: string]: unknown } | null>;
  scrollSourceRef: React.MutableRefObject<'sidebar' | 'gantt' | null>;
  scrollSyncTimerRef: React.MutableRefObject<number | null>;
  listScrollTopRef: React.MutableRefObject<number>;
  lastRenderScrollTopRef: React.MutableRefObject<number>;
}

export function useGanttScrollAndZoom({
  visibleTasks,
  visibleTasksRef,
  gridStart,
  dateToIndexMap,
  dateToIndexMapRef,
  dayWidth,
  dayWidthRef,
  setDayWidth,
  isFitMode,
  setIsFitMode,
  selectedTaskId,
  setSelectedTaskId,
  scrollContainerRef,
  sidebarListRef,
  ganttListRef,
  scrollSourceRef,
  scrollSyncTimerRef,
  listScrollTopRef,
  lastRenderScrollTopRef,
}: UseGanttScrollAndZoomParams) {
  const initialScrollRetryCountRef = useRef(0);

  const clearScrollSource = useCallback(() => {
    if (scrollSyncTimerRef.current !== null) {
      cancelAnimationFrame(scrollSyncTimerRef.current);
    }
    scrollSyncTimerRef.current = requestAnimationFrame(() => {
      scrollSyncTimerRef.current = null;
      scrollSourceRef.current = null;
    });
  }, [scrollSyncTimerRef, scrollSourceRef]);

  type ScrollEventOrOffset = number | { scrollOffset?: number; scrollUpdateWasRequested?: boolean; currentTarget?: { scrollTop: number }; target?: { scrollTop: number } };

  const handleSidebarScroll = useCallback((e: ScrollEventOrOffset) => {
    if (scrollSourceRef.current !== null) return;

    let scrollOffset = 0;
    let wasRequested = false;

    if (typeof e === 'number') {
      scrollOffset = e;
    } else if (e && typeof e.scrollOffset === 'number') {
      scrollOffset = e.scrollOffset;
      wasRequested = e.scrollUpdateWasRequested || false;
    } else if (e?.currentTarget && typeof e.currentTarget.scrollTop === 'number') {
      scrollOffset = e.currentTarget.scrollTop;
    } else if (e?.target && typeof e.target.scrollTop === 'number') {
      scrollOffset = e.target.scrollTop;
    } else {
      return;
    }

    if (wasRequested) return;
    if (Math.abs(scrollOffset - listScrollTopRef.current) <= 1) return;

    scrollSourceRef.current = 'sidebar';
    listScrollTopRef.current = scrollOffset;

    if (ganttListRef.current?.element && Math.abs(ganttListRef.current.element.scrollTop - scrollOffset) > 1) {
      ganttListRef.current.element.scrollTop = scrollOffset;
    }

    if (Math.abs(scrollOffset - lastRenderScrollTopRef.current) > 2) {
      lastRenderScrollTopRef.current = scrollOffset;
    }

    clearScrollSource();
  }, [clearScrollSource, ganttListRef, lastRenderScrollTopRef, listScrollTopRef, scrollSourceRef]);

  const handleGanttScroll = useCallback((e: ScrollEventOrOffset) => {
    if (scrollSourceRef.current !== null) return;

    let scrollOffset = 0;
    let wasRequested = false;

    if (typeof e === 'number') {
      scrollOffset = e;
    } else if (e && typeof e.scrollOffset === 'number') {
      scrollOffset = e.scrollOffset;
      wasRequested = e.scrollUpdateWasRequested || false;
    } else if (e?.currentTarget && typeof e.currentTarget.scrollTop === 'number') {
      scrollOffset = e.currentTarget.scrollTop;
    } else if (e?.target && typeof e.target.scrollTop === 'number') {
      scrollOffset = e.target.scrollTop;
    } else {
      return;
    }

    if (wasRequested) return;

    scrollSourceRef.current = 'gantt';
    listScrollTopRef.current = scrollOffset;

    if (sidebarListRef.current?.element && Math.abs(sidebarListRef.current.element.scrollTop - scrollOffset) > 1) {
      sidebarListRef.current.element.scrollTop = scrollOffset;
    }

    if (Math.abs(scrollOffset - lastRenderScrollTopRef.current) > 2) {
      lastRenderScrollTopRef.current = scrollOffset;
    }

    clearScrollSource();
  }, [clearScrollSource, lastRenderScrollTopRef, listScrollTopRef, scrollSourceRef, sidebarListRef]);

  const handleWheel = useCallback((e: React.WheelEvent<Element> | React.WheelEvent<HTMLElement> | WheelEvent) => {
    if (e.ctrlKey) {
      e.preventDefault();
      setIsFitMode(false);
      const zoomSpeed = 0.0012;
      const scaleChange = 1 - e.deltaY * zoomSpeed;
      setDayWidth(prev => {
        let next = prev * scaleChange;
        if (next < 8) next = 8;
        if (next > 260) next = 260;
        return next;
      });
      return;
    }
    if (!e.shiftKey) {
      const container = ganttListRef.current?.element;
      if (!container) return;
      e.preventDefault();
      const newTop = Math.max(0, container.scrollTop + e.deltaY);
      scrollSourceRef.current = 'gantt';
      container.scrollTop = newTop;
      if (sidebarListRef.current?.element) {
        sidebarListRef.current.element.scrollTop = newTop;
      }
      listScrollTopRef.current = newTop;
      if (Math.abs(newTop - lastRenderScrollTopRef.current) > 2) {
        lastRenderScrollTopRef.current = newTop;
      }
      clearScrollSource();
    }
  }, [clearScrollSource, ganttListRef, lastRenderScrollTopRef, listScrollTopRef, scrollSourceRef, setDayWidth, setIsFitMode, sidebarListRef]);

  const scrollToInitialView = useCallback(() => {
    const container = scrollContainerRef.current || ganttListRef.current?.element;
    if (!container || container.isConnected === false) {
      if (initialScrollRetryCountRef.current < 15) {
        initialScrollRetryCountRef.current++;
        setTimeout(() => {
          scrollToInitialView();
        }, 50);
      }
      return;
    }

    const currentTasks = visibleTasksRef.current && visibleTasksRef.current.length > 0
      ? visibleTasksRef.current
      : visibleTasks;
    const dateMap = dateToIndexMapRef.current && Object.keys(dateToIndexMapRef.current).length > 0
      ? dateToIndexMapRef.current
      : dateToIndexMap;

    if (Object.keys(dateMap).length === 0 && currentTasks.length > 0) {
      if (initialScrollRetryCountRef.current < 15) {
        initialScrollRetryCountRef.current++;
        setTimeout(() => {
          scrollToInitialView();
        }, 50);
        return;
      }
    }

    initialScrollRetryCountRef.current = 0;

    const currentDayWidth = dayWidthRef.current || dayWidth;
    const { targetTop, targetLeft } = calculateInitialGanttScroll(
      currentTasks,
      dateMap,
      todayStr,
      currentDayWidth,
      48
    );

    scrollSourceRef.current = 'gantt';

    container.scrollTop = targetTop;
    const sidebarEl = sidebarListRef.current?.element ||
                      (sidebarListRef.current?.scrollTop !== undefined ? sidebarListRef.current : null);
    if (sidebarEl) {
      sidebarEl.scrollTop = targetTop;
    }
    listScrollTopRef.current = targetTop;
    lastRenderScrollTopRef.current = targetTop;

    container.scrollTo({
      left: targetLeft,
      top: targetTop,
      behavior: 'smooth'
    });

    clearScrollSource();
  }, [visibleTasks, visibleTasksRef, dateToIndexMap, dateToIndexMapRef, dayWidth, dayWidthRef, clearScrollSource, scrollContainerRef, ganttListRef, scrollSourceRef, sidebarListRef, listScrollTopRef, lastRenderScrollTopRef]);

  const toggleZoomToFit = useCallback(() => {
    if (isFitMode) {
      setIsFitMode(false);
      setDayWidth(28);
      setTimeout(() => {
        scrollToInitialView();
      }, 80);
      return;
    }

    if (!scrollContainerRef.current || visibleTasks.length === 0) return;
    const dates = visibleTasks
      .filter(t => t.start || t.end)
      .flatMap(t => [parseDate(t.start || t.end).getTime(), parseDate(t.end || t.start).getTime()])
      .filter(ts => !isNaN(ts));
    if (dates.length === 0) return;
    const projectStart = new Date(Math.min(...dates));
    const projectEnd = new Date(Math.max(...dates));
    const projectDuration = diffDays(formatDate(projectStart), formatDate(projectEnd)) + 1;
    if (isNaN(projectDuration) || projectDuration <= 0) return;
    const containerWidthVal = scrollContainerRef.current.clientWidth;
    const margin = 6;
    const idealDayWidth = Math.min(80, Math.max(4, Math.floor(containerWidthVal / (projectDuration + margin))));
    setIsFitMode(true);
    setDayWidth(idealDayWidth);
    setTimeout(() => {
      if (scrollContainerRef.current) {
        const daysFromGridStart = diffDays(formatDate(gridStart), formatDate(projectStart));
        const scrollPos = (daysFromGridStart - margin / 2) * idealDayWidth;
        scrollContainerRef.current.scrollTo({ left: Math.max(0, scrollPos), behavior: 'smooth' });
      }
    }, 50);
  }, [isFitMode, visibleTasks, gridStart, scrollToInitialView, scrollContainerRef, setDayWidth, setIsFitMode]);

  const scrollToToday = useCallback((options?: { onlyHorizontal?: boolean; scrollVerticallyToActive?: boolean }) => {
    if (options?.scrollVerticallyToActive) {
      scrollToInitialView();
      return;
    }
    const container = scrollContainerRef.current;
    if (!container) return;
    const today = todayStr;
    const dateMap = dateToIndexMapRef.current && Object.keys(dateToIndexMapRef.current).length > 0
      ? dateToIndexMapRef.current
      : dateToIndexMap;
    if (dateMap[today] !== undefined) {
      const currentDayWidth = dayWidthRef.current || dayWidth;
      const scrollPos = Math.max(0, dateMap[today] * currentDayWidth - 120);
      if (options?.onlyHorizontal) {
        container.scrollTo({ left: scrollPos, behavior: 'smooth' });
      } else {
        scrollToInitialView();
      }
    } else {
      scrollToInitialView();
    }
  }, [dateToIndexMap, dateToIndexMapRef, dayWidth, dayWidthRef, scrollContainerRef, scrollToInitialView]);

  const zoomIn = useCallback(() => {
    setIsFitMode(false);
    setDayWidth(prev => Math.min(200, prev * 1.25));
  }, [setDayWidth, setIsFitMode]);

  const zoomOut = useCallback(() => {
    setIsFitMode(false);
    setDayWidth(prev => Math.max(8, prev / 1.25));
  }, [setDayWidth, setIsFitMode]);

  const scrollByDays = useCallback((days: number) => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: days * dayWidth, behavior: 'smooth' });
    }
  }, [dayWidth, scrollContainerRef]);

  const scrollToTask = useCallback((taskId: string | number, horizontalOnly: boolean = false) => {
    if (!scrollContainerRef.current) return false;

    if (selectedTaskId !== null && String(selectedTaskId) === String(taskId)) {
      setSelectedTaskId(null);
      return true;
    }

    setSelectedTaskId(taskId);

    const taskIndex = visibleTasks.findIndex(t => t.id.toString() === taskId.toString());
    if (taskIndex === -1) return false;

    const task = visibleTasks[taskIndex];
    const rowHeight = 48;
    const targetTop = Math.max(0, taskIndex * rowHeight - rowHeight * 2);

    const taskStartIndex = dateToIndexMapRef.current[task.start] || 0;

    const DEFAULT_ZOOM = 28;
    const needsZoomReset = dayWidthRef.current !== DEFAULT_ZOOM;
    if (needsZoomReset) {
      setDayWidth(DEFAULT_ZOOM);
    }

    const targetLeft = Math.max(0, taskStartIndex * DEFAULT_ZOOM - DEFAULT_ZOOM * 3);

    scrollSourceRef.current = 'gantt';

    if (!horizontalOnly) {
      scrollContainerRef.current.scrollTop = targetTop;

      if (sidebarListRef.current?.element) {
        sidebarListRef.current.element.scrollTop = targetTop;
      }

      listScrollTopRef.current = targetTop;
      if (Math.abs(targetTop - lastRenderScrollTopRef.current) > 2) {
        lastRenderScrollTopRef.current = targetTop;
      }
    }

    if (needsZoomReset) {
      setTimeout(() => {
        if (scrollContainerRef.current) {
          scrollContainerRef.current.scrollLeft = targetLeft;
          clearScrollSource();
        }
      }, 50);
    } else {
      scrollContainerRef.current.scrollLeft = targetLeft;
      clearScrollSource();
    }

    return true;
  }, [visibleTasks, clearScrollSource, selectedTaskId, scrollContainerRef, setSelectedTaskId, dateToIndexMapRef, dayWidthRef, setDayWidth, scrollSourceRef, sidebarListRef, listScrollTopRef, lastRenderScrollTopRef]);

  return {
    clearScrollSource,
    handleSidebarScroll,
    handleGanttScroll,
    handleWheel,
    scrollToInitialView,
    toggleZoomToFit,
    scrollToToday,
    zoomIn,
    zoomOut,
    scrollByDays,
    scrollToTask,
  };
}
